// Raccordement du synchroniseur à l'application : instance unique (coffre chiffré dont la clé reste dans le
// stockage de session, file et métadonnées en stockage local), contexte React, bandeau d'état réseau et de la
// file, mémoire chiffrée de l'état de session pour l'ouverture hors connexion pendant 24 h.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, ErreurApi } from '../api/client';
import type { EtatSession } from '../api/types';
import { CoffreLocal } from './chiffrement';
import { FileHorsLigne, LIBELLES_ETATS_OPERATION } from './file';
import { ErreurTransport, Synchroniseur, type EtatSynchro, type OperationEnvoi, type PaquetServeur, type ResultatServeur, type StockageLocal } from './synchroniseur';

/** Stockage de remplacement quand le navigateur refuse l'accès (navigation privée stricte) : rien n'est conservé. */
class StockageMemoire implements StockageLocal {
  private m = new Map<string, string>();
  getItem(c: string) {
    return this.m.get(c) ?? null;
  }
  setItem(c: string, v: string) {
    this.m.set(c, v);
  }
  removeItem(c: string) {
    this.m.delete(c);
  }
}

function stockage(type: 'local' | 'session'): StockageLocal {
  try {
    const s = type === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
    s.getItem('ct-essai');
    return s;
  } catch {
    return new StockageMemoire();
  }
}

/** Transport par l'API de l'application (cookie de session, jeton CSRF). */
export const transportApi = {
  async envoyer(operations: OperationEnvoi[]): Promise<ResultatServeur[]> {
    try {
      return (await api<{ resultats: ResultatServeur[] }>('/api/v1/sync', { corps: { operations } })).donnees.resultats;
    } catch (e) {
      if (e instanceof ErreurApi && e.statut === 401) throw new ErreurTransport('Session expirée : reconnectez-vous pour envoyer', true);
      throw new ErreurTransport(e instanceof Error ? e.message : 'Envoi interrompu');
    }
  },
  async paquet(versionConnue: string | null): Promise<PaquetServeur> {
    return (await api<PaquetServeur>(`/api/v1/sync/paquet${versionConnue ? `?version=${encodeURIComponent(versionConnue)}` : ''}`)).donnees;
  },
};

let instance: { synchroniseur: Synchroniseur; file: FileHorsLigne } | null = null;

/** Instance unique, créée à la première utilisation (IndexedDB et WebCrypto requis). */
export function horsLigne() {
  if (!instance) {
    const file = new FileHorsLigne(new CoffreLocal(stockage('session')), stockage('local'));
    instance = { file, synchroniseur: new Synchroniseur({ file, transport: transportApi, stockage: stockage('local') }) };
  }
  return instance;
}

const CLE_ETAT_SESSION = 'etat-session';

/** Mémorise, chiffré, l'état de la dernière session confirmée par le serveur. */
export async function memoriserEtatSession(e: EtatSession): Promise<void> {
  if (!e.connecte || !e.utilisateur) return;
  try {
    const { file, synchroniseur } = horsLigne();
    synchroniseur.validerSession(e.utilisateur.id);
    await file.ecrireDonnee(CLE_ETAT_SESSION, e, null);
  } catch {
    // Stockage local indisponible : l'application reste utilisable en ligne.
  }
}

/** État de session pour une ouverture hors connexion : seulement si la session locale a moins de 24 h. */
export async function etatSessionHorsLigne(): Promise<EtatSession | null> {
  try {
    const { file, synchroniseur } = horsLigne();
    const d = await file.lireDonnee<EtatSession>(CLE_ETAT_SESSION);
    if (!d || synchroniseur.sessionLocaleExpiree(d.contenu.utilisateur?.id)) return null;
    return { ...d.contenu, horsLigne: true };
  } catch {
    return null;
  }
}

export async function oublierSessionLocale(): Promise<void> {
  try {
    const { file, synchroniseur } = horsLigne();
    synchroniseur.oublierSession();
    await file.effacerDonnees();
  } catch {
    // Rien à oublier.
  }
}

interface ValeurHorsLigne {
  synchroniseur: Synchroniseur;
  etat: EtatSynchro;
}

const Contexte = createContext<ValeurHorsLigne | null>(null);

/** Démarre la reprise (ouverture, retour du réseau) et rafraîchit les écrans après chaque synchronisation. */
export function FournisseurHorsLigne({ children }: { children: ReactNode }) {
  const { synchroniseur } = useMemo(() => horsLigne(), []);
  const [etat, setEtat] = useState<EtatSynchro>(synchroniseur.etat());
  const client = useQueryClient();
  useEffect(() => synchroniseur.abonner(setEtat), [synchroniseur]);
  useEffect(() => {
    let arret: (() => void) | null = null;
    let actif = true;
    void synchroniseur.demarrer().then((f) => {
      if (actif) arret = f;
      else f();
    });
    return () => {
      actif = false;
      arret?.();
    };
  }, [synchroniseur]);
  const derniere = etat.derniereSynchro;
  useEffect(() => {
    if (derniere) void client.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
  }, [derniere, client]);
  return <Contexte.Provider value={{ synchroniseur, etat }}>{children}</Contexte.Provider>;
}

export function useHorsLigne(): ValeurHorsLigne {
  const v = useContext(Contexte);
  if (!v) throw new Error('useHorsLigne hors du fournisseur hors connexion');
  return v;
}

/** Bandeau d'état réseau et de la file : affiché seulement quand il y a quelque chose à dire. */
export function BandeauHorsLigne({ horsLigneSession }: { horsLigneSession?: boolean }) {
  const { etat, synchroniseur } = useHorsLigne();
  const enAttente = etat.compteurs.enregistre_local + etat.compteurs.en_attente_envoi + etat.compteurs.envoye;
  const aTraiter = etat.compteurs.conflit + etat.compteurs.refuse;
  if (etat.perdus.length > 0) {
    return (
      <div className="message avertissement" role="alert">
        <span>
          <strong>Des enregistrements locaux ont été effacés par le système de l’appareil</strong> ({etat.perdus.length}). Vérifiez ce que le serveur a reçu dans la page de synchronisation.
        </span>
        <Link className="bouton" to="/synchronisation">
          Voir la synchronisation
        </Link>
        <button type="button" className="bouton discret" onClick={() => void synchroniseur.prendreActeDeLaPurge()}>
          J’ai pris connaissance
        </button>
      </div>
    );
  }
  if (etat.enLigne && !horsLigneSession && enAttente === 0 && aTraiter === 0) return null;
  const texte = !etat.enLigne || horsLigneSession
    ? `Hors connexion${etat.derniereSynchro ? `, dernière synchronisation ${new Date(etat.derniereSynchro).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}${enAttente ? ` : ${enAttente} enregistrement${enAttente > 1 ? 's' : ''} sur cet appareil, envoi automatique au retour du réseau` : ''}`
    : etat.enCours
      ? `Synchronisation en cours (${enAttente} en attente)`
      : enAttente
        ? `${enAttente} enregistrement${enAttente > 1 ? 's' : ''} ${LIBELLES_ETATS_OPERATION.en_attente_envoi.toLowerCase()}`
        : `${aTraiter} enregistrement${aTraiter > 1 ? 's' : ''} à revoir (conflit ou refus)`;
  return (
    <div className={`message ${!etat.enLigne || horsLigneSession || aTraiter ? 'avertissement' : 'info'}`} role="status" aria-live="polite">
      <span>{texte}</span>
      <Link className="bouton discret" to="/synchronisation">
        Détail
      </Link>
      {etat.enLigne && !horsLigneSession && enAttente > 0 && !etat.enCours && (
        <button type="button" className="bouton" onClick={() => void synchroniseur.synchroniser()}>
          Envoyer maintenant
        </button>
      )}
    </div>
  );
}
